import useAPPayments from "@nkr/store/reports/hooks/use-ap-payments";
import ArApNav from "../ar-ap-nav";
import { DropdownButton, MenuItem, Well } from "react-bootstrap";
import { ReportContainer, ReportWell } from "../styles";
import { ReportWrapper } from "../accounts-payable/styles";
import { AgGridReact } from "ag-grid-react";
import { CellClassParams, ColDef, FirstDataRenderedEvent, ICellRendererParams, IHeaderParams, SortChangedEvent, ValueGetterParams } from "ag-grid-community";
import { IAccountsPayable } from "@nkr/models/reports";
import SortableHeaderComponent from "@nkr/views/admin/center/list/common/sortable-header";
import RankRenderer from "@nkr/views/admin/center/list/common/rank-renderer";
import BankInfoModal from "./bank-info-modal";
import ActionsButton from "./actions-button";
import { useRef } from "react";
import useExportPaymentList, { excelStyles } from "@nkr/store/reports/hooks/use-export-payment-list";
import ConfirmationFileModal from "./confirmation-file-modal";
import DetailsModal from "./details-modal";
import { ArStatusCell, arStatusValueGetter } from "../accounts-payable/ar-status-cell";



export default function AccountsPayablePayment({location}) {

    const { list, compareBankInfo, updateAchInfo, exportPaymentFile, importConfirmationFile, markPaid } = useAPPayments();
    const gridApiRef = useRef<any>(null);
    const selectedIdsRef = useRef<Set<number>>(new Set());
    const { onGridReady, exportPaymentList } = useExportPaymentList();

    const fmtValue = (value: number) => {
        if (value == 0) {
            return <>-</>;
        }

        return <>{value.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
    };

    const columns: ColDef[] = [
        {
            colId: 'rank',
            headerName: 'R#',
            headerClass: 'list-header',
            cellClass: 'list-cell-center',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => params.node.rowPinned ? null : <RankRenderer {...params} />,
            width: 50,
            suppressSizeToFit: true,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
        },
        {
            colId: 'select',
            headerClass: 'list-header list-header-checkbox',
            cellClass: 'list-cell-center',
            width: 25,
            suppressSizeToFit: true,
            sortable: false,
            resizable: false,
            checkboxSelection: true,
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
            suppressMenu: true,
        },
        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerName: 'Center',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => params.node.rowPinned ? 'Totals:' : params.value,
            width: 155,
            cellClass: 'list-cell-center',
            sortable: true,
            resizable: false
        },
        {
            colId: 'achRoutingNumber',
            field: 'achRoutingNumber',
            headerName: 'achRoutingNumber',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>System ABA<br/>Routing Number</SortableHeaderComponent>,
            width: 155,
            cellClass: (params: CellClassParams) =>
                params.data.compAchRoutingNumber && params.data.compAchRoutingNumber !== params.data.achRoutingNumber
                    ? 'list-cell-center bg-risk'
                    : 'list-cell-center',
            sortable: true,
            resizable: false
        },
        {
            colId: 'compAchRoutingNumber',
            field: 'compAchRoutingNumber',
            headerName: 'compAchRoutingNumber',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Chase ABA<br/>Routing Number</SortableHeaderComponent>,
            width: 155,
            cellClass: (params: CellClassParams) =>
                params.data.compAchRoutingNumber && params.data.compAchRoutingNumber !== params.data.achRoutingNumber
                    ? 'list-cell-center bg-risk'
                    : 'list-cell-center',
            sortable: true,
            resizable: false
        },
        {
            colId: 'achAccountNumber',
            field: 'achAccountNumber',
            headerName: 'achAccountNumber',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>System ABA<br/>Account Number</SortableHeaderComponent>,
            width: 155,
            cellClass: (params: CellClassParams) =>
                params.data.compAchAccountNumber && params.data.compAchAccountNumber !== params.data.achAccountNumber
                    ? 'list-cell-center bg-risk'
                    : 'list-cell-center',
            sortable: true,
            resizable: false
        },
        {
            colId: 'compAchAccountNumber',
            field: 'compAchAccountNumber',
            headerName: 'compAchAccountNumber',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Chase ABA<br/>Account Number</SortableHeaderComponent>,
            width: 155,
            cellClass: (params: CellClassParams) =>
                params.data.compAchAccountNumber && params.data.compAchAccountNumber !== params.data.achAccountNumber
                    ? 'list-cell-center bg-risk'
                    : 'list-cell-center',
            sortable: true,
            resizable: false
        },
        {
            colId: 'payNow',
            field: 'payNow',
            headerClass: 'list-header-right',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Pay Now</SortableHeaderComponent>,
            width: 140,
            cellClass: (params: CellClassParams) =>
                params.data.confirmationAmount != null && params.data.confirmationAmount !== params.data.payNow
                    ? ['list-cell-right', 'currency', 'bg-risk']
                    : ['list-cell-right', 'currency'],
            sortable: true,
            sortingOrder: ['desc', 'asc'],
            resizable: false,
            cellRendererFramework: (params: ICellRendererParams) => fmtValue(params.value)
        },
        {
            colId: 'centerHasLatePaymentsInLastYear',
            field: 'centerHasLatePaymentsInLastYear',
            headerName: 'AR Status',
            headerClass: 'list-header-right',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>AR Status</SortableHeaderComponent>,
            width: 100,
            cellClass: 'list-cell-center',
            sortable: true,
            sortingOrder: ['desc', 'asc'],
            resizable: false,
            cellRendererFramework: ArStatusCell,
            valueGetter: arStatusValueGetter
        },
        {
            colId: 'confirmationAmount',
            field: 'confirmationAmount',
            headerClass: 'list-header-right',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Confirmation<br/>Amount</SortableHeaderComponent>,
            width: 140,
            cellClass: (params: CellClassParams) =>
                params.data.confirmationAmount != null && params.data.confirmationAmount !== params.data.payNow
                    ? ['list-cell-right', 'currency', 'bg-risk']
                    : ['list-cell-right', 'currency'],
            sortable: true,
            resizable: false,
            cellRendererFramework: (params: ICellRendererParams) => params.value != null ? fmtValue(params.value) : null
        },
        {
            colId: 'confirmationNumber',
            field: 'confirmationNumber',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Chase Txn #</SortableHeaderComponent>,
            width: 155,
            cellClass: 'list-cell-center',
            sortable: true,
            resizable: false
        },
        {
            colId: 'actions',
            headerName: '',
            headerClass: 'list-header',
            width: 75,
            sortable: false,
            resizable: false,
            cellClass: 'list-cell-center',
            cellStyle: { overflow: 'visible', zIndex: 9999 },
            suppressMenu: true,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.node.rowPinned) return null;
                const d:IAccountsPayable = params.data;
                return <ActionsButton ap={d} onUpdateAchInfo={updateAchInfo} />;
            }
        },
        {
            colId: 'detail',
            headerName: '',
            headerClass: 'list-header',
            width: 75,
            sortable: false,
            resizable: false,
            cellClass: 'list-cell-center',
            cellStyle: { overflow: 'visible', zIndex: 9999 },
            suppressMenu: true,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.node.rowPinned) return null;
                const d:IAccountsPayable = params.data;
                return <DetailsModal ap={d} onMarkPaid={markPaid} />;
            }
        },
    ];

    return (
        <div className='container-fluid'>
            <ArApNav href={location.pathname} />
            <div style={ReportContainer}>
                <Well bsSize="sm" style={ReportWell}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>

                    </div>
                    <div>
                        <DropdownButton title="ACH Payment Actions" id="dropdown-size-medium" bsStyle="primary">
                            <BankInfoModal onBankInfoUploaded={compareBankInfo} />
                            <MenuItem onClick={exportPaymentList}>Export Payment List</MenuItem>
                            <MenuItem divider />
                            <MenuItem onClick={() => exportPaymentFile(gridApiRef.current?.getSelectedRows() ?? [])}>Export Chase ACH Trxns</MenuItem>
                            <ConfirmationFileModal onConfirmationFileUploaded={importConfirmationFile} />
                        </DropdownButton>
                    </div>
                </Well>
                <ReportWrapper className='ag-theme-bootstrap'>
                    <AgGridReact
                        columnDefs={columns}
                        rowSelection="multiple"
                        onSelectionChanged={(e) => {
                            const rows = e.api.getSelectedRows();
                            if (rows.length > 0)
                                selectedIdsRef.current = new Set(rows.map((r: IAccountsPayable) => r.centerId));
                        }}
                        defaultColDef={{}}
                        rowData={list}
                        pinnedTopRowData={[{
                            payNow: list.reduce((sum, p) => sum + p.payNow, 0),
                            confirmationAmount: list.some(p => p.confirmationAmount != null)
                                ? list.reduce((sum, p) => sum + (p.confirmationAmount ?? 0), 0)
                                : null
                        }]}
                        headerHeight={60}
                        groupHeaderHeight={60}
                        rowHeight={30}
                        suppressRowClickSelection={true}
                        suppressCellSelection={true}
                        excelStyles={excelStyles}
                        noRowsOverlayComponentFramework={() => <div>No records found...</div>}
                        onGridReady={(e) => { gridApiRef.current = e.api; onGridReady(e); }}
                        onFirstDataRendered={(e: FirstDataRenderedEvent) => { e.api.selectAll(); e.api.sizeColumnsToFit(); }}
                        onGridSizeChanged={(e) => e.api.sizeColumnsToFit()}
                        onRowDataChanged={(e) => {
                            e.api.forEachNode(node => {
                                if (selectedIdsRef.current.has(node.data.centerId))
                                    node.setSelected(true);
                            });
                            e.api.sizeColumnsToFit();
                        }}
                        onSortChanged={(e: SortChangedEvent) => { e.api.refreshCells(); }}
                        getRowStyle={(params: any) => {
                            if (params.node.rowPinned) {
                                return { backgroundColor: 'lightblue', fontWeight: 700 } as any;
                            }
                        }}
                    />
                </ReportWrapper>
            </div>
        </div>
    );
}
